import { API_CONFIG } from '../config/api';
import { hasAnalyticsConsent } from './analytics';
import { getMetaAttribution, isPaidMetaLanding } from '../utils/metaAttribution';
import { getStoredUtm } from '../utils/utm';

const SESSION_PAGE_VIEW_KEY = 'meta_capi_pageview_sent';

export type MetaCapiRelayEventName =
  | 'PageView'
  | 'ViewContent'
  | 'InitiateCheckout'
  | 'AuthFailed'
  | 'Lead';

type RelayParams = {
  eventId?: string;
  method?: string;
  error?: string;
  placement?: string;
};

/** Shared event_id helper for Pixel ↔ CAPI deduplication. */
export function createMetaEventId(eventName: MetaCapiRelayEventName, suffix?: string): string {
  const base = suffix || `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  return `${eventName.toLowerCase()}_${base}`;
}

function sanitizeEventSourceUrl(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

/** Once per browser session for paid Meta landings. */
export function shouldSendMetaLandingPageView(): boolean {
  if (typeof sessionStorage === 'undefined' || !isPaidMetaLanding()) {
    return false;
  }
  if (sessionStorage.getItem(SESSION_PAGE_VIEW_KEY) === '1') {
    return false;
  }
  sessionStorage.setItem(SESSION_PAGE_VIEW_KEY, '1');
  return true;
}

/**
 * Server-side Meta CAPI relay. Fire-and-forget; never blocks UX.
 * PageView is consent-free on paid Meta landings only; other events require consent + paid Meta attribution.
 */
export function relayMetaCapiEvent(
  eventName: MetaCapiRelayEventName,
  params?: RelayParams,
): void {
  if (typeof window === 'undefined' || import.meta.env.VITE_ENV === 'LOCAL') {
    return;
  }

  if (eventName !== 'PageView') {
    if (!hasAnalyticsConsent() || !isPaidMetaLanding()) {
      return;
    }
  }

  const utm = getStoredUtm();
  const meta = getMetaAttribution();
  const eventId = params?.eventId || createMetaEventId(eventName);

  const body: Record<string, string | undefined> = {
    eventName,
    eventId,
    eventSourceUrl: sanitizeEventSourceUrl(),
    utmSource: utm?.utm_source,
    utmMedium: utm?.utm_medium,
    utmCampaign: utm?.utm_campaign,
    utmTerm: utm?.utm_term,
    utmContent: utm?.utm_content,
    initialDesign: utm?.initialDesign,
    fbp: meta.fbp,
    fbc: meta.fbc,
    fbclid: meta.fbclid,
    method: params?.method,
    error: params?.error,
    placement: params?.placement,
  };

  fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.META_EVENT}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {
    // non-blocking
  });
}
